import React, { useCallback, useRef, useState } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import { ediphiPalette } from "../../../styles/colors";
import { BodyText } from "../../../shared/text/BodyText.styled";

type OstFileDropZoneProps = {
  onFileSelected: (file: File) => void;
  disabled?: boolean;
  /** Parsing in progress: inert, with a spinner in place of the upload icon. */
  loading?: boolean;
};

const ACCEPTED_EXTENSIONS = [".ost", ".xml"];

function isAcceptedFile(file: File): boolean {
  const name = file.name.toLowerCase();
  return ACCEPTED_EXTENSIONS.some((ext) => name.endsWith(ext));
}

const OstFileDropZone: React.FC<OstFileDropZoneProps> = ({ onFileSelected, disabled, loading }) => {
  const isInert = disabled || loading;
  const [isDragOver, setIsDragOver] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFile = useCallback(
    (file: File) => {
      if (!isAcceptedFile(file)) return;
      onFileSelected(file);
    },
    [onFileSelected]
  );

  const handleDragOver = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (!isInert) setIsDragOver(true);
    },
    [isInert]
  );

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(false);
      if (isInert) return;
      const file = e.dataTransfer.files[0];
      if (file) handleFile(file);
    },
    [isInert, handleFile]
  );

  const handleClick = useCallback(() => {
    if (isInert) return;
    inputRef.current?.click();
  }, [isInert]);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) handleFile(file);
      if (inputRef.current) inputRef.current.value = "";
    },
    [handleFile]
  );

  return (
    <Box sx={{ width: "100%" }}>
      <BodyText sx={{ mb: 2 }}>Import an OnScreen Takeoff (.ost) export file.</BodyText>
      <Box
        onClick={handleClick}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 1,
          py: 3,
          px: 2,
          border: "2px dashed",
          borderColor: isDragOver ? ediphiPalette.brand500 : ediphiPalette.ediphiSelectBorder,
          borderRadius: "8px",
          backgroundColor: isDragOver ? ediphiPalette.ediphiTextButtonBgHover : ediphiPalette.brand25,
          cursor: isInert ? "default" : "pointer",
          // `disabled`, not `isInert`: dimming says "unavailable", which is true of a zone with
          // nothing to upload into and false of one that is parsing. `opacity` composites the whole
          // subtree, so keying it on loading too would fade the spinner and "Parsing file…" — the
          // two things that state what is happening. The drop is already refused while loading.
          opacity: disabled ? 0.5 : 1,
          transition: "border-color 0.2s, background-color 0.2s",
          "&:hover": isInert
            ? {}
            : {
                borderColor: ediphiPalette.brand400,
                backgroundColor: ediphiPalette.ediphiTextButtonBgHover,
              },
        }}
      >
        {loading ? (
          <CircularProgress size={32} sx={{ color: ediphiPalette.brand500 }} />
        ) : (
          <UploadFileOutlinedIcon sx={{ fontSize: 32, color: ediphiPalette.brand500 }} />
        )}
        <Typography sx={{ fontSize: "14px", fontWeight: 600, color: ediphiPalette.mainTextColor }}>
          {loading ? "Parsing file..." : isDragOver ? "Drop file here" : "Drag & drop .ost file"}
        </Typography>
        {/* Hidden rather than unmounted so the box keeps its height while parsing. */}
        <Typography
          sx={{ fontSize: "12px", color: ediphiPalette.greyText, visibility: loading ? "hidden" : "visible" }}
        >
          or click to browse
        </Typography>
      </Box>
      <input ref={inputRef} type="file" accept=".ost,.xml" style={{ display: "none" }} onChange={handleInputChange} />
    </Box>
  );
};

export default OstFileDropZone;
